/**
 * @fileoverview A React component that displays a form for creating a new account.
 * The component allows users to enter a verification code, provide a name and password,
 * and select their consent for various terms and conditions.
 *
 * @example
 * function ParentComponent() {
 *   return <CreateAccount />;
 * }
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import NovoicDashboardLogo from '../assets/novoic-dashboard-logo.svg'
import { useUserAPI } from '../hooks/useUserAPI'
import { useCheckVerificationCodeQuery, useVerifyMutation } from '../services/users'
import { AccountForm } from './AccountForm'
import { ConsentForm } from './ConsentForm'

/**
 * @function CreateAccount
 * @description Component for creating a new account.
 * @returns {JSX.Element | null} - The CreateAccount component.
 */
export default function CreateAccount() {
    const [searchParams] = useSearchParams()
    const code = searchParams.get('code')

    const { isError, isLoading } = useCheckVerificationCodeQuery({ code })
    const [verify] = useVerifyMutation()
    const userAPI = useUserAPI()
    const navigate = useNavigate()

    const [step, setStep] = useState(1)
    const [isPending, setIsPending] = useState(false)
    const [password, setPassword] = useState('')
    const [repeatPassword, setRepeatPassword] = useState('')
    const [passwordValid, setPasswordValid] = useState(false)
    const formRef = useRef<HTMLFormElement>(null)

    const [consent, setConsent] = useState({
        cookies: false,
        emails: false,
        tracking: false,
        acknowledge: false,
        tos: false,
    })

    const passwordRequirements = useMemo(
        () => [
            {
                test: (password: string) => password.length >= 8,
                description: 'At least 8 characters',
            },
            {
                test: (password: string) => /[a-z]/.test(password),
                description: 'At least 1 lowercase character',
            },
            {
                test: (password: string) => /[A-Z]/.test(password),
                description: 'At least 1 uppercase character',
            },
            {
                test: (password: string) => /\d/.test(password),
                description: 'At least 1 number',
            },
            {
                test: (password: string) => /[!@#$%^&*-]/.test(password),
                description: 'At least 1 special character (!@#$%^&*)',
            },
        ],
        []
    )

    useEffect(() => {
        setPasswordValid(
            passwordRequirements.every((req) => req.test(password)) && password === repeatPassword
        )
    }, [password, repeatPassword, passwordRequirements])

    const allConsentsGiven = Object.values(consent).every((value) => value)

    if (isError) {
        setTimeout(() => navigate('/inviteExpired'))
    }

    if (isLoading) {
        return null
    }

    const handleConsentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setConsent({ ...consent, [event.target.name]: event.target.checked })
    }

    const handleContinue = () => {
        if (step === 1) {
            setStep(2)
        } else {
            formRef.current.requestSubmit()
        }
    }

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setIsPending(true)

        const formData = new FormData(e.currentTarget)
        const payload: Record<string, string> = {}

        for (const [key, value] of formData.entries()) {
            payload[key] = value as string
        }

        const data = await verify({
            code,
            name: payload.name,
            password: payload.password,
        }).unwrap()
        userAPI.logIn(data.user, data.intercom, data.token)
        setIsPending(false)
        setTimeout(() => navigate('/app'))
    }

    return (
        <div className="nv-grid min-h-screen">
            <div className="lg-container max-h-full items-center content-center justify-center col-start-1 md:col-start-2 lg:col-start-4">
                <div className="w-full max-w-[464px] space-y-8">
                    <div className="flex flex-col items-center gap-y-4">
                        <NovoicDashboardLogo className="w-56" />
                        <h2 className="text-center text-3xl font-bold font-serif text-gray-900">
                            Create your account
                        </h2>
                    </div>
                    {step === 1 ? (
                        <ConsentForm
                            consent={consent}
                            handleConsentChange={handleConsentChange}
                            handleContinue={handleContinue}
                            allConsentsGiven={allConsentsGiven}
                        />
                    ) : (
                        <AccountForm
                            formRef={formRef}
                            onSubmit={onSubmit}
                            password={password}
                            setPassword={setPassword}
                            repeatPassword={repeatPassword}
                            setRepeatPassword={setRepeatPassword}
                            passwordRequirements={passwordRequirements}
                            passwordValid={passwordValid}
                            isPending={isPending}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}
